import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Paper,
  Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tooltip, Typography,
} from '@mui/material';
import {
  Close as CloseIcon, Insights as InsightsIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;

const OUTCOME_COLOR = {
  VALID_FIRST_PASS: 'success',
  VALID_AFTER_REPAIR: 'warning',
  INVALID: 'error',
  ERROR: 'error',
};

/** "Valid first pass" reads better than VALID_FIRST_PASS in a table nobody wrote the enum for. */
const outcomeLabel = (outcome) => (outcome || '')
  .toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const when = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const seconds = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

/**
 * Admin: what people are actually asking FlowGate to build.
 *
 * <p>Two lists, because there are two front doors and they are stored differently. "Builder" is
 * the assistant in the app, one row per conversation rather than per turn, a single turn out of
 * context ("add a postcode field") says nothing, and the brief that opened the conversation is
 * the thing worth reading. "WhatsApp" is the concierge: someone messages the public number and
 * gets a flow back.
 *
 * <p>The column that earns its place is the one with no flow attached: a conversation that never
 * became a saved flow is somebody who asked for something and walked away, which is the most
 * useful row on the page.
 *
 * <p>These are other people's briefs. They stay on this screen, never put them in an analytics
 * property or a log line.
 */
const UsageActivityPanel = () => {
  const { authenticatedFetch } = useAuth();

  const [tab, setTab] = useState('builder');
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [concierge, setConcierge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null); // { kind: 'builder'|'whatsapp', data }
  const [openLoading, setOpenLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [builderRes, conciergeRes] = await Promise.all([
        authenticatedFetch(`${API_URL}/api/admin/activity/conversations?limit=100`),
        authenticatedFetch(`${API_URL}/api/admin/activity/concierge?limit=100`),
      ]);
      if (!builderRes.ok || !conciergeRes.ok) {
        throw new Error(builderRes.status === 403 || conciergeRes.status === 403
          ? 'You do not have permission to see this'
          : 'Failed to load activity');
      }
      const builder = await builderRes.json();
      setConversations(builder.items || []);
      setTotal(builder.total || 0);
      setConcierge(await conciergeRes.json());
    } catch (e) {
      setError(e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
    // authenticatedFetch is recreated every render; a dependency here would reload in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConversation = async (sessionId) => {
    setOpenLoading(true);
    setOpen({ kind: 'builder', data: null });
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/admin/activity/conversations/${sessionId}`);
      if (!response.ok) throw new Error('Could not load that conversation');
      setOpen({ kind: 'builder', data: await response.json() });
    } catch (e) {
      setOpen(null);
      setError(e.message);
    } finally {
      setOpenLoading(false);
    }
  };

  const renderBuilder = () => (
    <Table size="small" data-testid="builder-conversations">
      <TableHead>
        <TableRow>
          <TableCell>What they asked for</TableCell>
          <TableCell>Who</TableCell>
          <TableCell align="right">Turns</TableCell>
          <TableCell>Outcome</TableCell>
          <TableCell>Became</TableCell>
          <TableCell align="right">Time</TableCell>
          <TableCell>When</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {conversations.map((row) => (
          <TableRow
            key={row.sessionId}
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => openConversation(row.sessionId)}
            data-testid={`conversation-${row.sessionId}`}
          >
            <TableCell sx={{ maxWidth: 380 }}>
              <Typography variant="body2" noWrap title={row.firstRequest}>
                {row.firstRequest || '-'}
              </Typography>
            </TableCell>
            <TableCell>
              {row.demo ? <Chip size="small" label="Demo" /> : (
                <Typography variant="body2" noWrap>{row.who}</Typography>
              )}
            </TableCell>
            <TableCell align="right">
              {row.turns}
              {row.repairTurns > 0 && (
                <Tooltip title={`${row.repairTurns} repair turn(s), the model had to be corrected`}>
                  <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                    +{row.repairTurns}
                  </Typography>
                </Tooltip>
              )}
            </TableCell>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                color={OUTCOME_COLOR[row.lastOutcome] || 'default'}
                label={outcomeLabel(row.lastOutcome)}
              />
            </TableCell>
            <TableCell>
              {row.flowId
                ? <Typography variant="body2" noWrap>{row.flowName || 'a flow'}</Typography>
                : (
                  <Tooltip title="This conversation never became a saved flow">
                    <Typography variant="body2" color="text.secondary">Not saved</Typography>
                  </Tooltip>
                )}
            </TableCell>
            <TableCell align="right">
              <Typography variant="body2" color="text.secondary">{seconds(row.durationMs)}</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2" color="text.secondary" noWrap>{when(row.lastTurnAt)}</Typography>
            </TableCell>
          </TableRow>
        ))}
        {/* Only when the load actually succeeded. "Nobody has generated a flow yet" under a red
            error alert is a lie about the data, we do not know what is there. */}
        {conversations.length === 0 && !error && (
          <TableRow>
            <TableCell colSpan={7}>
              <Typography variant="body2" color="text.secondary">
                Nobody has generated a flow yet.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const renderConcierge = () => (
    <Table size="small" data-testid="concierge-conversations">
      <TableHead>
        <TableRow>
          <TableCell>Number</TableCell>
          <TableCell>First thing they said</TableCell>
          <TableCell align="right">Messages</TableCell>
          <TableCell>Became</TableCell>
          <TableCell>Last message</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {concierge.map((row) => (
          <TableRow
            key={row.id}
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => setOpen({ kind: 'whatsapp', data: row })}
            data-testid={`concierge-${row.id}`}
          >
            <TableCell><Typography variant="body2">{row.contact}</Typography></TableCell>
            <TableCell sx={{ maxWidth: 380 }}>
              <Typography variant="body2" noWrap>
                {row.transcript?.find((m) => m.role === 'user')?.text || '-'}
              </Typography>
            </TableCell>
            <TableCell align="right">{row.messageCount}</TableCell>
            <TableCell>
              {row.flowId
                ? <Typography variant="body2" noWrap>{row.flowName || 'a flow'}</Typography>
                : <Typography variant="body2" color="text.secondary">Not saved</Typography>}
            </TableCell>
            <TableCell>
              <Typography variant="body2" color="text.secondary" noWrap>{when(row.lastMessageAt)}</Typography>
            </TableCell>
          </TableRow>
        ))}
        {concierge.length === 0 && !error && (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography variant="body2" color="text.secondary">
                Nobody has messaged the WhatsApp number yet.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const bubble = (role, text, screens, key, meta) => (
    <Box key={key} sx={{ display: 'flex', justifyContent: role === 'user' ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Box sx={{
        maxWidth: '78%',
        p: 1.25,
        borderRadius: 2,
        bgcolor: role === 'user' ? 'primary.main' : 'action.hover',
        color: role === 'user' ? 'primary.contrastText' : 'text.primary',
      }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text || '-'}</Typography>
        {screens?.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {screens.map((screen) => (
              <Chip key={screen} size="small" label={screen} />
            ))}
          </Stack>
        )}
        {meta}
      </Box>
    </Box>
  );

  const renderTranscript = () => {
    if (openLoading || !open?.data) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
    }
    if (open.kind === 'whatsapp') {
      return (
        <Box data-testid="transcript-whatsapp">
          {open.data.transcript.map((message, index) =>
            bubble(message.role, message.text, message.screens, index, null))}
        </Box>
      );
    }
    return (
      <Box data-testid="transcript-builder">
        {open.data.turns.map((turn) => (
          <React.Fragment key={turn.turnIndex}>
            {bubble('user', turn.request, null, `u${turn.turnIndex}`, null)}
            {bubble('assistant', turn.message, turn.screens, `a${turn.turnIndex}`, (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={OUTCOME_COLOR[turn.outcome] || 'default'}
                  label={outcomeLabel(turn.outcome)}
                />
                <Chip size="small" variant="outlined" label={seconds(turn.durationMs)} />
                <Chip size="small" variant="outlined" label={`${turn.outputTokens} out`} />
              </Stack>
            ))}
            {turn.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid={`turn-errors-${turn.turnIndex}`}>
                {/* What the model got wrong before it got it right, the reason this table exists. */}
                {turn.errors.join(' · ')}
              </Alert>
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <InsightsIcon color="action" />
          <Typography variant="h6">What people are building</Typography>
        </Stack>
        <IconButton onClick={load} disabled={loading} aria-label="Refresh"><RefreshIcon /></IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every conversation with the generator, across all accounts. Click one to read it.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="activity-error">{error}</Alert>}

      <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 1 }}>
        <Tab
          value="builder"
          label={`Builder${total ? ` (${total})` : ''}`}
          data-testid="tab-builder"
        />
        <Tab
          value="whatsapp"
          label={`WhatsApp${concierge.length ? ` (${concierge.length})` : ''}`}
          data-testid="tab-whatsapp"
        />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          {tab === 'builder' ? renderBuilder() : renderConcierge()}
        </Box>
      )}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {open?.kind === 'whatsapp' ? `WhatsApp, ${open?.data?.contact ?? ''}` : 'Builder conversation'}
          <IconButton
            onClick={() => setOpen(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderTranscript()}</DialogContent>
      </Dialog>
    </Paper>
  );
};

export default UsageActivityPanel;
